import React from "react";

import { faSparkles, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { PopupKey } from "@performyard/service-contracts";

import { SuggestedActions } from "./SuggestedActions";

import { useDismissPopup, usePopupDismissed } from "../../../api/hooks";

type DismissiblePopupProps = {
	popupKey: PopupKey;
	enabled: boolean;
	title: string;
	action?: {
		label: string;
		onClick: () => void;
	};
};

/**
 * Callout above the Ask PerformYard trigger, shown on every visit while `enabled`
 * until the user dismisses it with the x (dismissal persisted per-user, keyed by `popupKey`).
 */
export const DismissiblePopup: React.FC<DismissiblePopupProps> = ({ popupKey, enabled, title, action }) => {
	const { data } = usePopupDismissed(popupKey, enabled);
	const { mutate: dismiss } = useDismissPopup(popupKey);

	if (!enabled || data?.dismissed !== false) {
		return null;
	}

	return (
		<div className="hide-in-pdf fixed right-6 bottom-24 z-[1099] w-72">
			<div className="relative flex items-start gap-2 rounded-lg border border-solid border-neutral-2 bg-white p-3 pr-8 shadow-[0_4px_20px_0_rgba(0,0,0,0.15)]">
				<FontAwesomeIcon
					icon={faSparkles}
					className="mt-0.5 shrink-0 text-primary-5"
					style={{ fontSize: "12px" }}
				/>
				<div className="flex flex-col items-start gap-0.5">
					<span className="text-paragraph-3 font-semibold text-neutral-900">{title}</span>
					{action && <SuggestedActions actions={[{ label: action.label, onClick: action.onClick }]} />}
				</div>
				<button
					type="button"
					onClick={() => dismiss()}
					aria-label="Dismiss"
					className="absolute top-2 right-2 cursor-pointer border-none bg-transparent p-1 text-neutral-5 hover:text-neutral-7"
				>
					<FontAwesomeIcon icon={faXmark} />
				</button>
			</div>
		</div>
	);
};

export default DismissiblePopup;
